import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';

interface FeatureToggles {
  socialEnabled: boolean;
  gamificationEnabled: boolean;
}

interface UseFeatureTogglesResult {
  socialEnabled: boolean;
  gamificationEnabled: boolean;
  loading: boolean;
  setSocialEnabled: (enabled: boolean) => Promise<void>;
  setGamificationEnabled: (enabled: boolean) => Promise<void>;
}

export function useFeatureToggles(): UseFeatureTogglesResult {
  const { user } = useAuth();
  const { toast } = useToast();
  const [toggles, setToggles] = useState<FeatureToggles>({
    socialEnabled: true,
    gamificationEnabled: true,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadPreferences = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('social_enabled, gamification_enabled')
          .eq('id', user.id)
          .single();

        if (error) throw error;

        // Cast to any to handle new columns not yet in generated types
        const profile = data as any;
        setToggles({
          socialEnabled: profile?.social_enabled ?? true,
          gamificationEnabled: profile?.gamification_enabled ?? true,
        });
      } catch (error) {
        console.error('Error loading feature toggles:', error);
        setToggles({ socialEnabled: true, gamificationEnabled: true });
      } finally {
        setLoading(false);
      }
    };

    loadPreferences();
  }, [user?.id]);

  const setSocialEnabled = async (enabled: boolean) => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from('profiles')
        .update({ social_enabled: enabled } as any)
        .eq('id', user.id);

      if (error) throw error;

      setToggles(prev => ({ ...prev, socialEnabled: enabled }));
      
      toast({
        title: enabled ? 'Social features enabled' : 'Social features disabled',
        description: enabled 
          ? 'Activity feed, followers, and leaderboards are now visible.'
          : 'Social features are now hidden from your experience.',
      });
    } catch (error) {
      console.error('Error updating social preference:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Could not update your preference. Please try again.',
      });
    }
  };

  const setGamificationEnabled = async (enabled: boolean) => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from('profiles')
        .update({ gamification_enabled: enabled } as any)
        .eq('id', user.id);

      if (error) throw error;

      setToggles(prev => ({ ...prev, gamificationEnabled: enabled }));
      
      toast({
        title: enabled ? 'Gamification enabled' : 'Gamification disabled',
        description: enabled 
          ? 'XP, levels, achievements, and quests are now visible.'
          : 'Gamification features are now hidden from your experience.',
      });
    } catch (error) {
      console.error('Error updating gamification preference:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Could not update your preference. Please try again.',
      });
    }
  };

  return {
    socialEnabled: toggles.socialEnabled,
    gamificationEnabled: toggles.gamificationEnabled,
    loading,
    setSocialEnabled,
    setGamificationEnabled,
  };
}
